import type {FooterData} from '@commercial-website/data-layer'
import type {Locale} from '@commercial-website/data-layer/locale'
import {locales} from '@commercial-website/data-layer/locale'
import {Instagram, Linkedin, Youtube} from '@commercial-website/icons'
import {Footer as UIFooter} from '@commercial-website/ui'
import {graphql, useStaticQuery} from 'gatsby'
import React, {type ComponentType, type SVGProps} from 'react'

import LocaleSwitcher from '@/components/locale-switcher'
import type {FooterQuery} from '@/graphql-types'
import {useLocalizedContent} from '@/hooks/use-localized-content'

export type FooterProps = {
    locales: Locale[]
    hideLinks?: boolean
    content: FooterData
}

// FooterData's socialLinks carry a `platform` key rather than an icon component, so the icon
// is resolved locally - mirrors Astro's FooterIsland.tsx.
const socialIconsByPlatform: Record<
    FooterData['socialLinks'][number]['platform'],
    ComponentType<SVGProps<SVGSVGElement>>
> = {
    linkedin: Linkedin,
    instagram: Instagram,
    youtube: Youtube,
}

function mapLinkGroups(content: FooterData, hideLinks: boolean) {
    if (hideLinks) {
        return []
    }

    return content.linkGroups.map(({heading, links}) => ({
        heading,
        links: links.map((link) => ({
            text: link.text,
            target: link.target,
            // FooterData links are already-resolved strings, so a plain anchor is enough here -
            // matching Astro's to-footer-link-groups.tsx pattern.
            render: ({children, ...props}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
                <a {...props} href={link.href}>
                    {children}
                </a>
            ),
        })),
    }))
}

export const Footer = ({content, locales: footerLocales, hideLinks = false}: FooterProps) => {
    const localeSwitcher = <LocaleSwitcher locales={footerLocales} />
    const address = (
        <>
            <p>{content.entity}</p>
            <br />
            {content.addressLines.map((line) => (
                <p key={line}>{line}</p>
            ))}
        </>
    )
    const linkGroups = mapLinkGroups(content, hideLinks)
    const socialLinks = content.socialLinks.map((link) => ({
        name: link.name,
        url: link.url,
        icon: socialIconsByPlatform[link.platform],
    }))

    return (
        <UIFooter
            localeSwitcher={localeSwitcher}
            address={address}
            linkGroups={linkGroups}
            socialLinks={socialLinks}
            terms={content.terms}
            copyright={content.copyright}
        />
    )
}

type Props = React.ComponentPropsWithoutRef<'footer'> & {
    hideLinks?: boolean
}

const FooterWithQuery = ({hideLinks}: Props) => {
    const {allFooterData} = useStaticQuery<FooterQuery>(query)
    const [node] = useLocalizedContent(allFooterData.nodes)
    const content: FooterData = {
        ...node,
        linkGroups: node.linkGroups.map(({heading, links}) => ({
            heading,
            links: links.map((link) => ({
                ...link,
                // GraphQL widens the target field to `string`, but FooterData only allows `'_blank'`.
                target: link.target === '_blank' ? '_blank' : undefined,
            })),
        })),
        // GraphQL widens `platform` to `string`; Sanity content is authored against the same
        // fixed platform set as `socialIconsByPlatform` above.
        socialLinks: node.socialLinks.map((link) => ({
            ...link,
            platform: link.platform as FooterData['socialLinks'][number]['platform'],
        })),
    }

    return <Footer hideLinks={hideLinks} locales={locales} content={content} />
}

const query = graphql`
    query Footer {
        allFooterData {
            nodes {
                lang
                entity
                addressLines
                terms
                copyright
                financialPartnerCopyright
                linkGroups {
                    heading
                    links {
                        text
                        href
                        target
                    }
                }
                socialLinks {
                    platform
                    name
                    url
                }
            }
        }
    }
`

export default FooterWithQuery
